import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from './Tooltip';
import { API_ENDPOINTS } from '../config/api';

const EnrollmentForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [classes, setClasses] = useState([]);
  const [formData, setFormData] = useState({
    // Student Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    className: '',
    previousSchool: '',
    medicalConditions: '',
    allergies: '',

    // Parent/Guardian Information
    parentFirstName: '',
    parentLastName: '',
    relationship: '',
    parentEmail: '',
    parentPhone: '',
    parentAltPhone: '',
    parentOccupation: '',

    // Address Information
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',

    // Emergency Contact
    emergencyName: '',
    emergencyRelationship: '',
    emergencyPhone: '',
    emergencyAltPhone: '',

    // Additional Information
    transportation: '',
    lunchProgram: '',
    specialNeeds: '',
    interests: '',
    comments: '',

    // Terms and Conditions
    agreeToTerms: false,
    agreeToPrivacy: false,
    agreeToPhotography: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}`);
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Student Information
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.age || formData.age < 3 || formData.age > 18) newErrors.age = 'Valid age (3-18) is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.className) newErrors.className = 'Class selection is required';
        break;

      case 2: // Parent Information
        if (!formData.parentFirstName.trim()) newErrors.parentFirstName = 'Parent first name is required';
        if (!formData.parentLastName.trim()) newErrors.parentLastName = 'Parent last name is required';
        if (!formData.relationship) newErrors.relationship = 'Relationship is required';
        if (!formData.parentEmail.trim()) newErrors.parentEmail = 'Parent email is required';
        if (!formData.parentPhone.trim()) newErrors.parentPhone = 'Parent phone is required';
        break;

      case 3: // Address & Emergency
        if (!formData.address.trim()) newErrors.address = 'Address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.emergencyName.trim()) newErrors.emergencyName = 'Emergency contact name is required';
        if (!formData.emergencyRelationship) newErrors.emergencyRelationship = 'Emergency relationship is required';
        if (!formData.emergencyPhone.trim()) newErrors.emergencyPhone = 'Emergency phone is required';
        break;

      case 4: // Terms & Conditions
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        if (!formData.agreeToPrivacy) newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(4)) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.ENROLLMENTS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          status: 'pending'
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        const errorData = await response.json();
        alert(`Enrollment submission failed: ${errorData.error || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error submitting enrollment:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, title: 'Student Info' },
      { number: 2, title: 'Parent Info' },
      { number: 3, title: 'Address & Emergency' },
      { number: 4, title: 'Review & Submit' }
    ];

    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-2 ${
                  currentStep >= step.number
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step.number}
              </div>
              <span className={`text-xs ${currentStep >= step.number ? 'text-primary font-medium' : 'text-gray-500'}`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const renderStudentInfo = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Student Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <label className="block text-sm font-medium text-gray-700">
              First Name *
            </label>
            <Tooltip content="Enter your child's legal first name as it appears on official documents. This will be used for all school records and communications." position="top">
              <span className="text-blue-600 text-sm font-bold cursor-help">Loading</span>
            </Tooltip>
          </div>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.firstName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter first name"
          />
          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.lastName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter last name"
          />
          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date of Birth *
          </label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Age *
          </label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            min="0"
            max="18"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.age ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Age (0-18)"
          />
          {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender *
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.gender ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Class *
          </label>
          <select
            name="className"
            value={formData.className}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.className ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Class</option>
            {classes.map((className) => (
              <option key={className} value={className}>{className}</option>
            ))}
          </select>
          {errors.className && <p className="text-red-500 text-sm mt-1">{errors.className}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Previous School (if any)
        </label>
        <input
          type="text"
          name="previousSchool"
          value={formData.previousSchool}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Previous school attended"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Medical Conditions
          </label>
          <textarea
            name="medicalConditions"
            value={formData.medicalConditions}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Any medical conditions or special health needs"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allergies
          </label>
          <textarea
            name="allergies"
            value={formData.allergies}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Any allergies (food, medication, environmental)"
          />
        </div>
      </div>
    </div>
  );

  const renderParentInfo = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Parent/Guardian Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            name="parentFirstName"
            value={formData.parentFirstName}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.parentFirstName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Parent's first name"
          />
          {errors.parentFirstName && <p className="text-red-500 text-sm mt-1">{errors.parentFirstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            name="parentLastName"
            value={formData.parentLastName}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.parentLastName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Parent's last name"
          />
          {errors.parentLastName && <p className="text-red-500 text-sm mt-1">{errors.parentLastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Relationship to Student *
          </label>
          <select
            name="relationship"
            value={formData.relationship}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.relationship ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Relationship</option>
            <option value="mother">Mother</option>
            <option value="father">Father</option>
            <option value="guardian">Legal Guardian</option>
            <option value="grandparent">Grandparent</option>
            <option value="other">Other</option>
          </select>
          {errors.relationship && <p className="text-red-500 text-sm mt-1">{errors.relationship}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Occupation
          </label>
          <input
            type="text"
            name="parentOccupation"
            value={formData.parentOccupation}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Parent's occupation"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="parentEmail"
            value={formData.parentEmail}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.parentEmail ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="parent@example.com"
          />
          {errors.parentEmail && <p className="text-red-500 text-sm mt-1">{errors.parentEmail}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Phone *
          </label>
          <input
            type="tel"
            name="parentPhone"
            value={formData.parentPhone}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.parentPhone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="+1 (555) 123-4567"
          />
          {errors.parentPhone && <p className="text-red-500 text-sm mt-1">{errors.parentPhone}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Alternative Phone
        </label>
        <input
          type="tel"
          name="parentAltPhone"
          value={formData.parentAltPhone}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Alternative contact number"
        />
      </div>
    </div>
  );

  const renderAddressEmergency = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Address & Emergency Contact</h3>

      {/* Address Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Residential Address</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.address ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Street address, apartment, suite, etc."
            />
            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="City"
            />
            {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State/Province
            </label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="State or Province"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postal Code
            </label>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="ZIP or Postal Code"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Country"
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact Section */}
      <div className="bg-red-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-red-800 mb-4">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="emergencyName"
              value={formData.emergencyName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                errors.emergencyName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Emergency contact full name"
            />
            {errors.emergencyName && <p className="text-red-500 text-sm mt-1">{errors.emergencyName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship to Student *
            </label>
            <select
              name="emergencyRelationship"
              value={formData.emergencyRelationship}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                errors.emergencyRelationship ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Relationship</option>
              <option value="mother">Mother</option>
              <option value="father">Father</option>
              <option value="grandparent">Grandparent</option>
              <option value="aunt">Aunt</option>
              <option value="uncle">Uncle</option>
              <option value="other">Other</option>
            </select>
            {errors.emergencyRelationship && <p className="text-red-500 text-sm mt-1">{errors.emergencyRelationship}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Phone *
            </label>
            <input
              type="tel"
              name="emergencyPhone"
              value={formData.emergencyPhone}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                errors.emergencyPhone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="+1 (555) 123-4567"
            />
            {errors.emergencyPhone && <p className="text-red-500 text-sm mt-1">{errors.emergencyPhone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alternative Phone
            </label>
            <input
              type="tel"
              name="emergencyAltPhone"
              value={formData.emergencyAltPhone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Alternative emergency contact"
            />
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-800">Additional Information</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transportation Needs
            </label>
            <select
              name="transportation"
              value={formData.transportation}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select Transportation</option>
              <option value="school_bus">School Bus</option>
              <option value="parent_transport">Parent Transportation</option>
              <option value="walking">Walking</option>
              <option value="public_transport">Public Transport</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lunch Program
            </label>
            <select
              name="lunchProgram"
              value={formData.lunchProgram}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select Lunch Option</option>
              <option value="school_lunch">School Lunch Program</option>
              <option value="bring_lunch">Bring Lunch from Home</option>
              <option value="reduced_price">Reduced Price Lunch</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Needs or Accommodations
          </label>
          <textarea
            name="specialNeeds"
            value={formData.specialNeeds}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Any special educational needs, accommodations, or support services required"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Student Interests & Hobbies
          </label>
          <textarea
            name="interests"
            value={formData.interests}
            onChange={handleInputChange}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Sports, arts, music, clubs, or other interests"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Comments
          </label>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Any additional information you'd like to share"
          />
        </div>
      </div>
    </div>
  );

  const renderReviewSubmit = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Review & Submit</h3>

      {/* Terms and Conditions */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Terms & Conditions</h4>

        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                I agree to the Terms and Conditions *
              </label>
              <p className="text-sm text-gray-600 mt-1">
                I have read and agree to abide by Creative Town Bilingual School's terms and conditions, including school policies, code of conduct, and academic requirements.
              </p>
            </div>
          </div>
          {errors.agreeToTerms && <p className="text-red-500 text-sm ml-7">{errors.agreeToTerms}</p>}

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              name="agreeToPrivacy"
              checked={formData.agreeToPrivacy}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                I agree to the Privacy Policy *
              </label>
              <p className="text-sm text-gray-600 mt-1">
                I consent to the collection and use of personal information as outlined in the school's privacy policy for educational and administrative purposes.
              </p>
            </div>
          </div>
          {errors.agreeToPrivacy && <p className="text-red-500 text-sm ml-7">{errors.agreeToPrivacy}</p>}

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              name="agreeToPhotography"
              checked={formData.agreeToPhotography}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                Photography/Media Release
              </label>
              <p className="text-sm text-gray-600 mt-1">
                I give permission for my child's photograph to be used in school publications, website, and social media for educational and promotional purposes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Summary */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-blue-800 mb-4">Application Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Student:</strong> {formData.firstName} {formData.lastName}
          </div>
          <div>
            <strong>Class:</strong> {formData.className}
          </div>
          <div>
            <strong>Age:</strong> {formData.age}
          </div>
          <div>
            <strong>Parent:</strong> {formData.parentFirstName} {formData.parentLastName}
          </div>
          <div>
            <strong>Email:</strong> {formData.parentEmail}
          </div>
          <div>
            <strong>Phone:</strong> {formData.parentPhone}
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-yellow-800 mb-3">Important Notice</h4>
        <ul className="text-sm text-yellow-700 space-y-2">
          <li>? This enrollment application will be reviewed by our admissions team</li>
          <li>? You will receive an email confirmation once your application is submitted</li>
          <li>? Processing may take 3-5 business days</li>
          <li>? You will be contacted for an interview or assessment if required</li>
          <li>? Final acceptance is subject to availability and meeting admission criteria</li>
        </ul>
      </div>
    </div>
  );

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-6xl mb-6">Success</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Enrollment Submitted Successfully!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for choosing Creative Town Bilingual School. Your enrollment application has been submitted and is being reviewed by our admissions team.
            </p>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">What happens nextLoading</h3>
              <ul className="text-sm text-blue-700 space-y-1 text-left">
                <li>? You'll receive a confirmation email within 24 hours</li>
                <li>? Our team will review your application (3-5 business days)</li>
                <li>? We'll contact you for any additional information needed</li>
                <li>? Final decision will be communicated via email and phone</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition duration-300"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Student Enrollment</h1>
          <p className="text-lg text-gray-600">
            Join the Creative Town Bilingual School family. Please fill out all required information carefully.
          </p>
        </div>

        {/* Progress Indicator */}
        {renderStepIndicator()}

        {/* Form Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          {currentStep === 1 && renderStudentInfo()}
          {currentStep === 2 && renderParentInfo()}
          {currentStep === 3 && renderAddressEmergency()}
          {currentStep === 4 && renderReviewSubmit()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Previous
          </button>

          <div className="text-sm text-gray-500">
            Step {currentStep} of 4
          </div>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-blue-700 transition duration-200"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </div>
              ) : (
                'Submit Enrollment'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentForm;
